import { Divider, Group, rem, Stack, Text, Tooltip } from '@mantine/core'
import {
  ArrowsOutIcon,
  RepeatIcon,
  RepeatOnceIcon,
  StackSimpleIcon,
  VirusIcon,
  type Icon,
} from '@phosphor-icons/react'
import type { ReactElement, ReactNode } from 'react'
import type {
  BoggleChallengeSpecialTileInteractionIcon,
  BoggleChallengeSpecialTileRevealEntry,
  BoggleChallengeSpecialTileSupplyIcon,
} from '~/boggle/config/specialTiles/challengeSpecialTileTypes'
import {
  BOGGLE_SPECIAL_TILE_REGISTRY,
  type BoggleSpecialTileKind,
  type BoggleSpecialTileVisualShape,
} from '~/boggle/engine/specialTiles'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'

const TILE_PREVIEW_SIZE = 58
const TILE_PREVIEW_FONT_SIZE = 24
const REVEAL_ICON_SIZE = 26
const REVEAL_ICON_GLYPH_SIZE = 22

const SUPPLY_ICON_BY_KEY = {
  fixed: StackSimpleIcon,
  regenerating: RepeatIcon,
  refill: RepeatOnceIcon,
} satisfies Record<BoggleChallengeSpecialTileSupplyIcon, Icon>

const INTERACTION_ICON_BY_KEY = {
  'spread-on-disuse': VirusIcon,
  'spread-on-use': ArrowsOutIcon,
} satisfies Record<BoggleChallengeSpecialTileInteractionIcon, Icon>

function getShapePoints(shape: Exclude<BoggleSpecialTileVisualShape, 'circle'>) {
  const sides = shape === 'hexagon' ? 6 : 5
  const radius = TILE_PREVIEW_SIZE * 0.38
  const center = TILE_PREVIEW_SIZE / 2
  const rotation = -Math.PI / 2

  return Array.from({ length: sides }, (_, index) => {
    const angle = rotation + (Math.PI * 2 * index) / sides
    const x = center + Math.cos(angle) * radius
    const y = center + Math.sin(angle) * radius

    return `${x},${y}`
  }).join(' ')
}

export function ChallengeSpecialTilePreview({
  kind,
  previewLetter,
}: {
  kind: BoggleSpecialTileKind
  previewLetter: string
}) {
  const definition = BOGGLE_SPECIAL_TILE_REGISTRY[kind]

  return (
    <svg
      aria-hidden="true"
      height={rem(TILE_PREVIEW_SIZE)}
      viewBox={`0 0 ${TILE_PREVIEW_SIZE} ${TILE_PREVIEW_SIZE}`}
      width={rem(TILE_PREVIEW_SIZE)}
    >
      {definition.tileShape === 'circle' ? (
        <circle
          cx={TILE_PREVIEW_SIZE / 2}
          cy={TILE_PREVIEW_SIZE / 2}
          fill={definition.previewFill}
          r={TILE_PREVIEW_SIZE * 0.38}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="2"
        />
      ) : (
        <polygon
          fill={definition.previewFill}
          points={getShapePoints(definition.tileShape)}
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="2"
        />
      )}
      <text
        dominantBaseline="central"
        fill={definition.previewTextColor}
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize={TILE_PREVIEW_FONT_SIZE}
        fontWeight="700"
        textAnchor="middle"
        x={TILE_PREVIEW_SIZE / 2}
        y={TILE_PREVIEW_SIZE / 2}
      >
        {previewLetter}
      </text>
    </svg>
  )
}

function ChallengeSpecialTileEntry({
  entry,
  previewLetter,
}: {
  entry: BoggleChallengeSpecialTileRevealEntry
  previewLetter: string
}) {
  return (
    <Stack gap={0} w="100%" bdrs={24}>
      <Group justify="center" align="flex-start" gap={0} wrap="nowrap" w="100%">
        <ChallengeSpecialTileTooltip
          label={entry.kindIntrinsicDescription}
          targetAriaLabel={entry.kindIntrinsicDescription}
          testId={`boggle-challenge-special-tile-${entry.kind}-${entry.behaviorId}`}
        >
          <Group
            component="span"
            justify="center"
            align="center"
            gap={0}
            w={rem(TILE_PREVIEW_SIZE)}
          >
            <ChallengeSpecialTilePreview
              kind={entry.kind}
              previewLetter={previewLetter}
            />
          </Group>
        </ChallengeSpecialTileTooltip>

        <Group gap={6}>
          <ChallengeSpecialTileTooltip
            label={entry.supply.tooltipDescription}
            targetAriaLabel={formatSpecialTileTooltipAriaLabel(
              entry.supply.tooltipDescription,
            )}
            testId={`boggle-challenge-special-tile-supply-${entry.kind}-${entry.behaviorId}`}
          >
            <ChallengeSpecialTileIcon
              icon={SUPPLY_ICON_BY_KEY[entry.supply.icon]}
              tone="supply"
            />
          </ChallengeSpecialTileTooltip>

          {entry.interaction ? (
            <ChallengeSpecialTileTooltip
              label={
                <Stack gap={6}>
                  <Text c="white" fz={15} fw={700} lh={1.2} ff="Tilt Neon, sans-serif">
                    {entry.interaction.name}
                  </Text>
                  <Stack gap={2}>
                    {entry.interaction.tooltipDescription.map(line => (
                      <Text
                        key={line}
                        c="rgba(255, 255, 255, 0.78)"
                        fz={13}
                        lh={1.1}
                        ff="Tilt Neon, sans-serif"
                      >
                        {line}
                      </Text>
                    ))}
                  </Stack>
                </Stack>
              }
              targetAriaLabel={`${entry.interaction.name}: ${formatSpecialTileTooltipAriaLabel(entry.interaction.tooltipDescription)}`}
              testId={`boggle-challenge-special-tile-interaction-${entry.kind}-${entry.behaviorId}`}
            >
              <ChallengeSpecialTileIcon
                icon={INTERACTION_ICON_BY_KEY[entry.interaction.icon]}
                tone="interaction"
              />
            </ChallengeSpecialTileTooltip>
          ) : null}
        </Group>
      </Group>
    </Stack>
  )
}

function ChallengeSpecialTileTooltip({
  children,
  label,
  targetAriaLabel,
  testId,
}: {
  children: ReactElement
  label: ReactNode
  targetAriaLabel: string
  testId: string
}) {
  return (
    <Tooltip
      bg="dark.8"
      c="white"
      events={{ hover: true, focus: true, touch: true }}
      ff="Tilt Neon, sans-serif"
      label={formatSpecialTileTooltipLabel(label)}
      multiline
      position="top"
      withinPortal={false}
    >
      <Group
        component="span"
        data-testid={testId}
        tabIndex={0}
        role="img"
        aria-label={targetAriaLabel}
        gap={0}
      >
        {children}
      </Group>
    </Tooltip>
  )
}

function formatSpecialTileTooltipLabel(label: ReactNode) {
  return Array.isArray(label)
    ? renderSpecialTileTooltipLines(label)
    : typeof label === 'string'
      ? renderSpecialTileTooltipLines([label])
      : label
}

function formatSpecialTileTooltipAriaLabel(lines: readonly string[]) {
  return lines.join(' ')
}

function renderSpecialTileTooltipLines(lines: readonly string[]) {
  return lines.length === 1 ? (
    <Text c="inherit" fz={15} lh={1.25} ff="Tilt Neon, sans-serif">
      {lines[0]}
    </Text>
  ) : (
    <Stack gap={2}>
      {lines.map(line => (
        <Text key={line} c="inherit" fz={15} lh={1.25} ff="Tilt Neon, sans-serif">
          {line}
        </Text>
      ))}
    </Stack>
  )
}

function ChallengeSpecialTileIcon({
  icon,
  tone,
}: {
  icon: Icon
  tone: 'interaction' | 'supply'
}) {
  return (
    <Group
      component="span"
      align="center"
      justify="center"
      gap={0}
      h={REVEAL_ICON_SIZE}
      w={REVEAL_ICON_SIZE}
      bdrs={10}
    >
      <IconGlyph
        color={tone === 'interaction' ? 'var(--mantine-color-orange-3)' : '#f2e8d9'}
        icon={icon}
        size={REVEAL_ICON_GLYPH_SIZE}
      />
    </Group>
  )
}

export function ChallengeSpecialTilesSection({
  entries,
}: {
  entries: readonly BoggleChallengeSpecialTileRevealEntry[]
}) {
  if (entries.length === 0) {
    return null
  }

  return (
    <Stack data-testid="boggle-challenge-special-tiles" gap={8} align="center" w="100%">
      <Divider label="Special Tiles" labelPosition="center" color="dark.5" w={270} />
      <Stack gap={10} w="100%" maw={360} px={26} pb={12}>
        {entries.map((entry, index) => (
          <ChallengeSpecialTileEntry
            entry={entry}
            key={`${entry.kind}-${entry.behaviorId}-${index}`}
            previewLetter={String.fromCharCode(65 + index)}
          />
        ))}
      </Stack>
    </Stack>
  )
}
